import {
  AdditionalInfoStatus,
  AddressType,
  AverageTransactionValue,
  BankVerificationSource,
  BankVerificationStatus,
  BusinessActivity,
  CountryCode,
  ExpectedMonthlyRevenue,
  Gender,
  IdentificationType,
  IdentityVerificationStatus,
  PartyType,
  RelationType,
  RiskProfileStatus,
  SettlementStatus,
  SourceOfIncome,
  UserStatus,
} from "Infrastructure/Api/Api";
import { partyLicenseSchema } from "Schemas/licensesSchema";
import {
  addOtherNationalitiesIssues,
  otherNationalitiesSchema,
} from "Schemas/otherNationalitiesSchema";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { relatedPartySchema } from "Schemas/relatedPartySchema";
import { Resources, type TFunction } from "Translations/Resources";
import { PersonalIdentification } from "Utils/PersonalIdentification";
import { z } from "zod";

export const getUserValidationSchema = (t: TFunction) =>
  z.object({
    userID: z.number(),
    partyPublicID: z.string(),
    login: z.email(t(Resources.Validation.InvalidLogin)),
    status: z.enum(UserStatus),
    hasIdentificationsUploaded: z.boolean(),
    verificationAdditionalInfoStatus: z.enum(AdditionalInfoStatus),
    verificationRiskProfile: z.enum(RiskProfileStatus),
    verificationIdentityVerificationStatus: z.enum(IdentityVerificationStatus),
    verificationBankVerificationStatus: z.enum(BankVerificationStatus),
    bankVerificationSource: z.enum(BankVerificationSource).nullish(),
    bankAccountNumber: z.string().nullish(),
    verificationDailyBuyLimit: z.number(),
    verificationMonthlyBuyLimit: z.number(),
  });

export const verificationUpdateValidationSchema = z.object({
  requestedUserID: z.number().nullish(),
  businessActivity: z.enum(BusinessActivity).nullish(),
  otherBusinessActivityDetails: z.string().nullish(),
  sourcesOfIncome: z.array(z.enum(SourceOfIncome)).nullish(),
  otherSourceOfIncomeDetails: z.string().nullish(),
  expectedMonthlyRevenue: z.enum(ExpectedMonthlyRevenue).nullish(),
  averageTransactionValue: z.enum(AverageTransactionValue).nullish(),
  taxResidency: z.enum(CountryCode).nullish(),
  isPoliticallyExposedPerson: z.boolean(),
  hasNonEUNationality: z.boolean().nullish(),
  hasNonEUResidence: z.boolean().nullish(),
  uboUnknown: z.boolean(),
});

export type UserVerificationUpdateFormData = z.infer<
  typeof verificationUpdateValidationSchema
>;

const getAddressValidationSchema = (
  t: TFunction,
  options?: PartyValidationOptions,
) =>
  z
    .object({
      addressID: z.number().nullish(),
      type: z.enum(AddressType),
      street: z.string().nullish(),
      streetNumber: z.string().nullish(),
      orientationNumber: z.string().nullish(),
      postalCode: z.string().nullish(),
      municipality: z.string().min(1, t(Resources.Validation.Required)),
    })
    .superRefine((address, ctx) => {
      // A person acting for a company is filed with the payment provider, which needs their full
      // residence — the same completeness the backend enforces on save. Villages without street
      // names are covered by the house number. Other address types stay optional.
      if (
        !options?.isResidenceRequired ||
        address.type !== AddressType.Permanent
      ) {
        return;
      }

      if (
        !address.street &&
        !address.streetNumber &&
        !address.orientationNumber
      ) {
        ctx.addIssue({
          code: "custom",
          message: t(Resources.Validation.Required),
          path: ["street"],
        });
      }

      if (!address.postalCode) {
        ctx.addIssue({
          code: "custom",
          message: t(Resources.Validation.Required),
          path: ["postalCode"],
        });
      }
    });

export type AddressFormData = z.infer<
  ReturnType<typeof getAddressValidationSchema>
>;

type PartyValidationOptions = {
  isPersonalNumberOptional?: boolean;
  isResidenceRequired?: boolean;
};

export const createPartyValidationSchema = (
  t: TFunction,
  type: PartyType,
  options?: PartyValidationOptions,
) => {
  const personalNumberSchema = options?.isPersonalNumberOptional
    ? z
        .string()
        .max(10, {
          message: t(Resources.Validation.PersonalNumber, { count: 10 }),
        })
        .nullish()
    : z.string().max(10, {
        message: t(Resources.Validation.PersonalNumber, { count: 10 }),
      });

  const baseSchema = {
    publicID: z.string(),
    phone: getPhoneSchema(t, { isRequired: false }),
    type: z.enum(PartyType),
    email: z.email(t(Resources.Validation.InvalidEmail)).nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    companyName: z.string().nullish(),
    taxNumber: z.string().nullish(),
    // IČ DPH — empty string means "not registered for VAT / not provided" (never null, same as the backend).
    // Declared here so the whole DTO round-trips through zod; a key missing from the schema is stripped on
    // submit and the backend would then overwrite the stored value with the default. Forms that do not start
    // from a party DTO must seed it as "" in their defaultValues.
    vatNumber: z.string(),
    companyNumber: z.string().nullish(),
    personalNumber: z.string().nullish(),
    birthDate: z.coerce.string().nullish(),
    gender: z.enum(Gender),
    birthPlace: z.string().nullish(),
    addresses: z.array(getAddressValidationSchema(t, options)),
    nationality: z.enum(CountryCode).nullish(),
    otherNationalities: otherNationalitiesSchema,
    partyIdentification: z
      .object({
        identification: z.enum(IdentificationType).nullish(),
        identificationNumber: z.string().nullish(),
        identificationValidFrom: z.string().nullish(),
        identificationValidTo: z.string().nullish(),
        identificationIssuer: z.string().nullish(),
        isIDCardApprovedByClient: z.boolean().nullish(),
        secondIdentification: z.enum(IdentificationType).nullish(),
        secondIdentificationNumber: z.string().nullish(),
        secondIdentificationValidFrom: z.string().nullish(),
        secondIdentificationValidTo: z.string().nullish(),
        secondIdentificationIssuer: z.string().nullish(),
      })
      .nullish(),
    isVATPayer: z.boolean().nullish(),
    isForeigner: z.boolean(),
    companyRepresentativeFirstName: z.string().nullish(),
    companyRepresentativeLastName: z.string().nullish(),
    companyRepresentativeBirthDate: z.string().nullish(),
    licenses: z.array(partyLicenseSchema),
    relatedParties: z.array(relatedPartySchema),
    hasIdentificationsUploaded: z.boolean().nullish(),
    settlementStatus: z.enum(SettlementStatus).nullish(),
  };

  if (type === PartyType.NaturalPerson || type === PartyType.SelfEmployed) {
    return z
      .object({
        ...baseSchema,
        firstName: z.string().min(1, t(Resources.Validation.Required)),
        email: z.email().nullish().or(z.literal("")), //Note: BE serves email on new party as "" for some reason
        personalNumber: personalNumberSchema,
        nationality: z.enum(CountryCode),
      })
      .superRefine((obj, ctx) => {
        addOtherNationalitiesIssues({
          ctx,
          t,
          nationality: obj.nationality,
          otherNationalities: obj.otherNationalities,
        });

        if (obj.personalNumber == null) {
          return;
        }

        const personalIdentificator = PersonalIdentification.getInfo(
          obj.personalNumber,
        );

        if (!personalIdentificator.IsValid) {
          ctx.addIssue({
            code: "custom",
            message: t(Resources.Validation.InvalidPersonalNumber),
            path: ["personalNumber"],
          });
        }
      });
  }

  if (type === PartyType.LegalEntity) {
    return z.object({
      ...baseSchema,
      companyName: z.string().min(1, t(Resources.Validation.Required)),
      companyNumber: z.string().min(1, t(Resources.Validation.Required)),
      email: z.email(t(Resources.Validation.InvalidEmail)),
    });
  }

  return z.object(baseSchema);
};

export type PartyFormData = z.infer<
  ReturnType<typeof createPartyValidationSchema>
>;

export type PartyFormDataInput = z.input<
  ReturnType<typeof createPartyValidationSchema>
>;

export const createRelatedPartyValidationSchema = (
  t: TFunction,
  type: PartyType,
  options?: PartyValidationOptions,
) =>
  createPartyValidationSchema(t, type, options).safeExtend({
    relationType: z.enum(RelationType),
    isOwner: z.boolean(),
  });

export type RelatedPartyFormData = z.infer<
  ReturnType<typeof createRelatedPartyValidationSchema>
>;
export type RelatedPartyFormDataInput = z.input<
  ReturnType<typeof createRelatedPartyValidationSchema>
>;

export const createBeneficialOwnerValidationSchema = (t: TFunction) =>
  createPartyValidationSchema(t, PartyType.NaturalPerson, {
    isPersonalNumberOptional: true,
  }).safeExtend({
    publicID: z.string(),
    type: z.enum(PartyType).refine((val) => val === PartyType.NaturalPerson),
    firstName: z.string().min(1, t(Resources.Validation.Required)),
    lastName: z.string().min(1, t(Resources.Validation.Required)),
    birthDate: z.coerce.string().min(1, t(Resources.Validation.Required)),
    nationality: z.enum(CountryCode),
    addresses: z.array(getAddressValidationSchema(t)),
    relationType: z.literal(RelationType.BeneficialOwner),
    isOwner: z.literal(true),
  });

export type BeneficialOwnerFormData = z.infer<
  ReturnType<typeof createBeneficialOwnerValidationSchema>
>;
export type BeneficialOwnerFormDataInput = z.input<
  ReturnType<typeof createBeneficialOwnerValidationSchema>
>;
